import { Routes } from '../utils/routerHelper';
import { formatAdminDateTime } from '../utils/adminDateTime';

/** Maintainer note: operator summaries here; full matrix in docs/ADMIN_COMMUNICATIONS_GUIDE.md */
export const ADMIN_GUIDE_INTRO =
  'Practical guidance for running your Challenge — communications, participants, moderation, and nominations.';

export interface AdminGuideLink {
  label: string;
  route: Routes;
  description?: string;
}

export interface AdminGuideMatrixRow {
  message: string;
  when: string;
  who: string;
}

export interface AdminGuideSection {
  id: string;
  title: string;
  intro: string;
  bullets?: string[];
  matrix?: AdminGuideMatrixRow[];
  links?: AdminGuideLink[];
}

export interface AdminGuideTiming {
  competitionStart?: string;
  competitionEnd?: string;
  launchWelcomeAt?: string;
  timezone?: string;
}

export interface AdminGuideBuildOptions {
  timing?: AdminGuideTiming;
  isStaging?: boolean;
  heroPledge?: { points: number; repeats: number } | null;
  teamsLabel?: string;
  teamLabel?: string;
}

/** Quick links shown at the top of the in-app guide (authorized administrators). */
export const ADMIN_GUIDE_QUICK_LINKS: AdminGuideLink[] = [
  { label: 'Communications', route: Routes.Communications },
  { label: 'Scheduled Communications', route: Routes.CommunicationsScheduled },
  { label: 'Send Message', route: Routes.SendMessage },
  { label: 'Email Templates', route: Routes.EmailTemplates },
  { label: 'Engagement Review', route: Routes.Moderation },
  { label: 'Hero Nominations', route: Routes.HeroNominations },
  { label: 'Notifications', route: Routes.Notifications }
];

const formatWhen = (iso: string | undefined, timezone?: string): string | undefined => {
  if (!iso) {
    return undefined;
  }
  const formatted = formatAdminDateTime(iso, timezone);
  if (timezone && formatted !== '—') {
    return `${formatted} (${timezone})`;
  }
  return formatted;
};

const buildTimingBullets = (timing?: AdminGuideTiming): string[] => {
  if (!timing || !timing.competitionStart) {
    return [
      'Challenge opening and welcome email times are shown on Communications → Scheduled Communications once your Challenge is configured.'
    ];
  }
  const bullets: string[] = [];
  const open = formatWhen(timing.competitionStart, timing.timezone);
  if (open) {
    bullets.push(`Challenge opens: ${open}`);
  }
  const welcomeAt = timing.launchWelcomeAt || timing.competitionStart;
  const welcome = formatWhen(welcomeAt, timing.timezone);
  if (welcome) {
    const sameAsOpen = !timing.launchWelcomeAt ||
      new Date(timing.launchWelcomeAt).getTime() === new Date(timing.competitionStart).getTime();
    bullets.push(
      sameAsOpen
        ? `Launch welcome email: ${welcome} (same as Challenge opening)`
        : `Launch welcome email: ${welcome}`
    );
  }
  if (timing.competitionEnd) {
    const end = formatWhen(timing.competitionEnd, timing.timezone);
    if (end) {
      bullets.push(`Challenge closes: ${end}`);
    }
  }
  return bullets;
};

const buildHeroBullets = (heroPledge?: { points: number; repeats: number } | null): string[] => {
  const base = [
    'Participants nominate colleagues through the Highlight an environmental hero action. Nominations require human review before anyone is featured publicly.',
    'Use status filters to find nominations after they leave the New queue.',
    'Open nomination detail to read the full reason and linked story.',
    'Featuring a nominee requires Challenge administrator approval.',
    'Nominees receive an in-app notification when nominated (and email when that template is enabled).'
  ];
  if (heroPledge && heroPledge.repeats > 0) {
    const repeatPoints = Math.min(heroPledge.points, 50);
    const repeatCount = Math.max(0, heroPledge.repeats - 1);
    const total = heroPledge.points + repeatPoints * repeatCount;
    base.unshift(
      `Scoring for this Challenge: ${heroPledge.points} points for the first hero nomination` +
      (repeatCount > 0
        ? `, ${repeatPoints} points for each of up to ${repeatCount} additional nomination(s) (${total} total across ${heroPledge.repeats}).`
        : '.')
    );
  } else {
    base.unshift(
      'Hero nomination points follow your Challenge\'s Highlight a Hero pledge settings (see Hero Nominations or pledge configuration).'
    );
  }
  return base;
};

export const buildAdminGuideSections = (options: AdminGuideBuildOptions = {}): AdminGuideSection[] => {
  const { timing, isStaging, heroPledge, teamsLabel, teamLabel } = options;
  const divisionWord = teamsLabel || 'Divisions';
  const singleDivisionWord = teamLabel || 'division';

  const troubleshootingBullets = [
    'Confirm the person was eligible (preregistered, account preferences, verified membership for broadcasts).',
    'Check whether the relevant email template is enabled under Email Templates.',
    'For automated launch emails, confirm the welcome message was due (at or after the configured launch welcome time).',
    'Open Communications → Scheduled Communications for sent/pending counts and the last automated communications check.',
    'Remember that likes and thread replies notify participants in-app only — they do not send email.',
    'Comment email goes to the story author only when they enable comment email under Account → Notifications.',
    'Confirm they are checking the same email address used to preregister or join.'
  ];
  if (isStaging) {
    troubleshootingBullets.push(
      'On this staging environment, outbound email is not delivered to real inboxes — messages are logged for testing instead.'
    );
  }

  return [
    {
      id: 'before-launch',
      title: 'Before your Challenge launches',
      intro:
        'This guide is available to authorized Challenge administrators (Challenge admins and host-organization administrators). Division captains can send division messages and use division-scoped Engagement review but do not see this full guide.',
      bullets: [
        'Check Challenge opening and closing dates.',
        'Open Communications → Scheduled Communications and confirm welcome timing.',
        'Review Signup Confirmation and Welcome email templates.',
        'Send yourself test copies from Email Templates.',
        'Confirm automated communications were checked recently and there are no warnings.',
        `Review departments/${divisionWord.toLowerCase()} and administrator access.`,
        'Review open moderation items under Engagement Review.',
        'Use Send Message for announcements after participants have joined.'
      ]
    },
    {
      id: 'communications',
      title: 'Communications',
      intro:
        'Your Challenge uses in-app notifications, automated lifecycle emails around launch, and administrator announcements. Likes and most thread replies stay in-app; story authors may receive comment email if they opt in.',
      matrix: [
        {
          message: 'Signup confirmation',
          when: 'Immediately after preregistration',
          who: 'Person who preregistered'
        },
        {
          message: 'Launch welcome',
          when: 'At your configured launch welcome time (see timing below)',
          who: 'Preregistered participants'
        },
        {
          message: 'Announcements',
          when: 'When an authorized admin or division captain sends one',
          who: 'Selected audience'
        },
        {
          message: 'Likes and comments',
          when: 'As participant activity occurs',
          who: 'Relevant story participants'
        }
      ],
      bullets: [
        ...buildTimingBullets(timing),
        'Repeated automated checks will not send duplicate welcome emails.',
        'Email Templates: edit preregistration and notification copy. Use test send to preview; subjects are prefixed [TEST] and deliver only to your email.',
        'Disable a template to temporarily prevent that email from being sent. Scheduled messages may become eligible again if the template is re-enabled.',
        'Send Message: always preview before sending. Flow is Compose → Preview → Send. Preview shows audience and message rendering; sending is immediate. Use Message History afterward to see what was sent. Broadcast history shows recipient counts; recipient email addresses are hidden from list views and are available only to authorized administrators in message details. Division captains may send only to their permitted audience and see their own sends in history.',
        'Participants manage email preferences under Account → Notifications. In-app notifications are always delivered.'
      ],
      links: [
        {
          label: 'Communications',
          route: Routes.Communications,
          description: 'Overview of announcements, scheduled email, templates, and recent broadcasts.'
        },
        {
          label: 'Scheduled Communications',
          route: Routes.CommunicationsScheduled,
          description: 'Launch timing, sent/pending counts, template status, and last automated check.'
        },
        {
          label: 'Email Templates',
          route: Routes.EmailTemplates,
          description: 'Edit, test, and manage lifecycle and notification email templates.'
        },
        {
          label: 'Send Message',
          route: Routes.SendMessage,
          description: 'Compose broadcasts and review send history.'
        },
        {
          label: 'Notifications',
          route: Routes.Notifications,
          description: 'Your in-app notification feed.'
        }
      ]
    },
    {
      id: 'participants',
      title: 'Participants & Teams',
      intro: `Participant signup, ${singleDivisionWord} selection, and team structure are configured for each Challenge.`,
      links: [
        { label: 'Totals', route: Routes.Totals, description: 'Participation and points reporting.' },
        { label: 'Departments', route: Routes.Departments, description: 'Department listing and leaderboard.' },
        { label: teamsLabel || 'Teams', route: Routes.Teams, description: `${divisionWord} listing for this Challenge.` }
      ]
    },
    {
      id: 'moderation',
      title: 'Moderation',
      intro:
        'Participants can flag content. Challenge administrators see full engagement feeds; division captains see division-scoped views.',
      bullets: [
        'Open — requires attention.',
        'Reviewed — you have reviewed the case; it leaves the Open queue but remains available under Reviewed or All.',
        'Dismissed — the report does not require action.',
        'Removed — content was removed.',
        'All — complete history.',
        'Marking a case Reviewed does not delete it.',
        'Open View details to inspect the actual content.',
        'Reporter identity is visible only to authorized Challenge administrators. Division captains do not receive reporter identity on captain-scoped views.',
        'Likes and comments on pledge stories generate participant notifications so authors and thread participants can reply.'
      ],
      links: [
        {
          label: 'Engagement Review',
          route: Routes.Moderation,
          description: 'Flags, likes, comments, and alerts.'
        }
      ]
    },
    {
      id: 'hero-nominations',
      title: 'Hero Nominations',
      intro: 'Review Highlight-a-Hero nominations from participants.',
      bullets: buildHeroBullets(heroPledge),
      links: [
        {
          label: 'Hero Nominations',
          route: Routes.HeroNominations,
          description: 'Review and approve nominations.'
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      intro: 'If someone says they did not receive an email:',
      bullets: troubleshootingBullets
    }
  ];
};

/** @deprecated Use buildAdminGuideSections() — static export for legacy tests only. */
export const ADMIN_GUIDE_SECTIONS = buildAdminGuideSections();
